import { KeyboardEvent } from 'react';
import { Folder, FileIcon, ArrowUp, ArrowDown, Copy, Check } from 'lucide-react';

import { FileEntry } from '@app/api/files/types';
import { useCopyToClipboard } from '@app/hooks/useCopyToClipboard';

import { SortOrder } from '../hooks/useFileBrowser';
import { LoadingIndicator } from './LoadingIndicator';

interface FileTableProps {
  contents: FileEntry[];
  currentPath: string;
  isLoading: boolean;
  sortOrder: SortOrder;
  isDirectoryBrowsable: (path: string) => boolean;
  navigateTo: (path: string) => void;
  getParentDirectory: (path: string) => string;
  onToggleSortOrder?: () => void;
  className?: string;
}

export function FileTable({
  contents,
  currentPath,
  isLoading,
  sortOrder = 'asc',
  isDirectoryBrowsable,
  navigateTo,
  getParentDirectory,
  onToggleSortOrder,
  className = '',
}: FileTableProps) {
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // Render sort indicator in the column header
  const renderSortIndicator = () => {
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1 inline-block" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1 inline-block" />
    );
  };

  // Create data URL from path
  const getDataUrl = (path: string) => {
    // Ensure the path starts with a slash for proper concatenation
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    return `https://data.jua.ai${formattedPath}`;
  };

  // Handle copying the data URL to clipboard
  const handleCopy = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = getDataUrl(path);
    copyToClipboard(url, path);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent, callback: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  // Handle keyboard navigation for copy button
  const handleCopyKeyDown = (e: KeyboardEvent, path: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation(); // Stop event from bubbling up to parent
      const url = getDataUrl(path);
      copyToClipboard(url, path);
    }
  };

  return (
    <div className={`min-h-[400px] relative overflow-x-auto ${className}`}>
      {/* Loading overlay */}
      {isLoading && <LoadingIndicator fullscreen message="Loading..." />}

      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
              onClick={onToggleSortOrder}
              tabIndex={0}
              onKeyDown={(e) => handleKeyDown(e, onToggleSortOrder || (() => {}))}
              role="button"
              aria-label="Sort by name"
            >
              <div className="flex items-center">
                Name
                {renderSortIndicator()}
              </div>
            </th>
            <th scope="col" className="relative px-6 py-3 w-24">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {/* Parent directory entry (if not at root) */}
          {!isLoading && currentPath !== '/' && (
            <tr
              className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E34290] focus:bg-gray-100 dark:focus:bg-gray-700 transition-colors duration-200"
              onClick={() => navigateTo(getParentDirectory(currentPath))}
              onKeyDown={(e) => handleKeyDown(e, () => navigateTo(getParentDirectory(currentPath)))}
              tabIndex={0}
              role="button"
              aria-label="Parent directory"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <Folder className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">..</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <span className="text-gray-400 dark:text-gray-500">-</span>
              </td>
            </tr>
          )}

          {/* Empty state */}
          {!isLoading && contents.length === 0 && (
            <tr>
              <td colSpan={2} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                This directory is empty
              </td>
            </tr>
          )}

          {/* Directory and file entries */}
          {!isLoading &&
            contents.map((entry) => {
              // Determine if this directory is clickable
              const isZarrSubdirectory =
                entry.type === 'directory' && !isDirectoryBrowsable(entry.path);
              const isClickable = entry.type === 'directory' && !isZarrSubdirectory;

              return (
                <tr
                  key={entry.path}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${
                    isClickable
                      ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E34290] focus:bg-gray-100 dark:focus:bg-gray-700'
                      : ''
                  }`}
                  onClick={isClickable ? () => navigateTo(entry.path) : undefined}
                  onKeyDown={
                    isClickable ? (e) => handleKeyDown(e, () => navigateTo(entry.path)) : undefined
                  }
                  tabIndex={isClickable ? 0 : undefined}
                  role={isClickable ? 'button' : undefined}
                  aria-label={isClickable ? `Navigate to ${entry.name}` : undefined}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center overflow-hidden">
                      {entry.type === 'directory' ? (
                        <Folder
                          className={`flex-shrink-0 h-5 w-5 mr-3 ${
                            isZarrSubdirectory
                              ? 'text-gray-400 dark:text-gray-500'
                              : 'text-[#E34290] dark:text-[#E34290]'
                          }`}
                        />
                      ) : (
                        <FileIcon className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400 mr-3" />
                      )}
                      <span
                        className={`text-sm truncate ${
                          isZarrSubdirectory
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {entry.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => handleCopy(entry.path, e)}
                      onKeyDown={(e) => handleCopyKeyDown(e, entry.path)}
                      className="text-[#E34290] hover:text-[#E34290]/80 dark:text-[#E34290] dark:hover:text-[#E34290]/80 inline-flex items-center focus:outline-none focus:ring-2 focus:ring-[#E34290] rounded-md px-2 py-1 transition-colors duration-200"
                      aria-label={`Copy URL for ${entry.name}`}
                      data-ph-capture-attribute="btn-file-table-copy"
                    >
                      {isCopied(entry.path) ? (
                        <>
                          <Check className="h-4 w-4 mr-1 text-green-600 dark:text-green-400" />
                          <span className="text-green-600 dark:text-green-400">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          <span>Copy URL</span>
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
